import { useMemo } from "react"

import { PortfolioPageObjClass } from "scrivito/Objs/PortfolioPage/PortfolioPageObjClass"
import { ProductObj } from "scrivito/Objs/Product/ProductObjClass"
import { ProductCategoryObj } from "scrivito/Objs/ProductCategory/ProductCategoryObjClass"
import { intersection, isEmpty } from "shared/lib/arrayUtils"
import { useFuzzySearch } from "shared/lib/useFuzzySearch"
import { PRODUCT_SEARCH_OPTIONS } from "shared/types/Product"

import { ProductFilterFormData, hasProductFilter } from "../types/ProductFilter"

function getProductTitle(product: ProductObj) {
  return product.get("title")
}

const emptyProducts: ProductObj[] = []

export function useProductFilter(
  filter: ProductFilterFormData,
  options?: { category?: ProductCategoryObj }
) {
  const { category } = options || {}

  const fuzzySearch = useFuzzySearch({
    getSearchableString: getProductTitle,
    options: PRODUCT_SEARCH_OPTIONS
  })

  let products = emptyProducts

  if (category) {
    products = category
      .orderedChildren()
      .flatMap(child => child.orderedChildren())
      .filter(product => !product?.get("isHiddenInPortfolio")) as ProductObj[]
  } else {
    products =
      (PortfolioPageObjClass.all()
        .first()
        ?.orderedChildren()
        .flatMap(child => child.orderedChildren())
        .flatMap(child => child.orderedChildren())
        .filter(product => !product?.get("isHiddenInPortfolio")) as
        | ProductObj[]
        | undefined) || emptyProducts
  }

  if (hasProductFilter(filter)) {
    products = products.filter(product => {
      if (filter.buildingTypes && !isEmpty(filter.buildingTypes)) {
        if (
          intersection(product.get("buildingTypes"), filter.buildingTypes)
            .length === 0
        ) {
          return false
        }
      }
      if (filter.roomTypes && !isEmpty(filter.roomTypes)) {
        if (
          intersection(product.get("roomTypes"), filter.roomTypes).length === 0
        ) {
          return false
        }
      }
      if (filter.productGroups && !isEmpty(filter.productGroups)) {
        const parentGroup = product.parent()?.contentId()
        if (!parentGroup || !filter.productGroups?.includes(parentGroup)) {
          return false
        }
      }
      return true
    })
  }

  return useMemo(() => {
    return fuzzySearch(products, filter?.searchTerm ?? "")
  }, [filter, products])
}
